import { inject, Injectable } from '@angular/core';
import {
	LOCAL_STORAGE_KEYS,
	LocalStorageService,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { AuthService } from './auth.service';

const PreferenceKeys = ['folderNavigationFilter'] as const;
export type PreferenceKey = (typeof PreferenceKeys)[number];

interface PreferencesStoreV1 {
	v: 1;
	scopes: Record<string, Record<string, unknown>>;
}

@Injectable({ providedIn: 'root' })
export class PreferencesService {
	private readonly localStorage = inject(LocalStorageService);
	private readonly organizationId$ = inject(ORGANIZATION_ID);
	private readonly authService = inject(AuthService);

	private scopeKey(): string | null {
		const orgId = this.organizationId$.value;
		const userId = this.authService.user?.id;
		if (!orgId || !userId) return null;
		return `${orgId}:${userId}`;
	}

	private readStore(): PreferencesStoreV1 {
		const raw = this.localStorage.getItem(
			LOCAL_STORAGE_KEYS.preferences.userState,
		);
		if (!raw) return { v: 1, scopes: {} };
		try {
			const parsed = JSON.parse(raw) as Partial<PreferencesStoreV1>;
			if (
				parsed?.v === 1 &&
				parsed.scopes &&
				typeof parsed.scopes === 'object'
			) {
				return {
					v: 1,
					scopes: parsed.scopes as Record<
						string,
						Record<string, unknown>
					>,
				};
			}
		} catch {
			// ignore corrupt payload
		}
		return { v: 1, scopes: {} };
	}

	private writeStore(store: PreferencesStoreV1): void {
		this.localStorage.setItem(
			LOCAL_STORAGE_KEYS.preferences.userState,
			JSON.stringify(store),
		);
	}

	/**
	 * Read a preference for the current org + user scope. Returns null if missing or unauthenticated.
	 */
	get<T = unknown>(key: PreferenceKey): T | null {
		const sk = this.scopeKey();
		if (!sk) return null;
		const scope = this.readStore().scopes[sk];
		if (!scope || !(key in scope)) return null;
		return scope[key] as T;
	}

	/**
	 * Persist a JSON-serializable value for the current org + user scope.
	 */
	set(key: PreferenceKey, value: unknown): void {
		const sk = this.scopeKey();
		if (!sk) return;
		const store = this.readStore();
		const prev = store.scopes[sk] ?? {};
		store.scopes[sk] = { ...prev, [key]: value };
		this.writeStore(store);
	}

	/**
	 * Remove one key from the current scope.
	 */
	remove(key: PreferenceKey): void {
		const sk = this.scopeKey();
		if (!sk) return;
		const store = this.readStore();
		const prev = store.scopes[sk];
		if (!prev || !(key in prev)) return;
		const next = { ...prev };
		delete next[key];
		store.scopes[sk] = next;
		this.writeStore(store);
	}
}
